import React, { useEffect, useMemo, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { FaTimes as X, FaTrash as Trash, FaPlus as Plus } from 'react-icons/fa'
import type { Device, DeviceCategory, Port, SignalType } from '@/types/devices'
import { CATEGORY_COLORS, CATEGORY_LABELS } from '@/constants/categories'
import { SIGNAL_TYPE_COLORS } from '@/constants/colors'
import { isBidirectionalPort } from '@/lib/presetToDevice'

interface EditDeviceDialogProps {
  isOpen: boolean
  onClose: () => void
  /**
   * The device being edited. `null` means this is a "create new
   * custom device" flow — the dialog renders with empty fields and
   * Save calls `onCreate` instead of `onUpdate`.
   */
  device: Device | null
  onUpdate?: (device: Device) => void
  onCreate?: (device: Device) => void
  /**
   * Optional prefill for the create-new flow. Used by photo ingest
   * to seed name/manufacturer/category/ports from Claude's best guess.
   */
  prefill?: Partial<Pick<Device, 'name' | 'manufacturer' | 'category'>> & {
    inputs?: Array<Omit<Port, 'id'>>
    outputs?: Array<Omit<Port, 'id'>>
  }
}

const CATEGORIES: DeviceCategory[] = [
  'interface',
  'mixer',
  'synth',
  'drum_machine',
  'sampler',
  'controller',
  'effects',
  'monitoring',
  'midi_interface',
  'groovebox',
  'semi_modular',
  'computer',
  'usb_hub',
  'network_switch',
  'recorder',
  'microphone',
]

const SIGNAL_TYPES: SignalType[] = [
  'audio',
  'midi',
  'sync',
  'cv_gate',
  'adat',
  'word_clock',
  'usb',
  'ethernet',
]

interface DraftPort {
  id: string
  name: string
  signalType: SignalType
  connectorFormat?: string
  bidirectional?: boolean
  direction: 'input' | 'output'
}

function makeId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function devicePortsToDraft(device: Device | null): DraftPort[] {
  if (!device) return []
  const seen = new Set<string>()
  const out: DraftPort[] = []
  // Keep a single row for bidirectional pairs — the twin port on the
  // other side collapses back on save.
  for (const p of device.inputs) {
    if (p.bidirectionalPairId && seen.has(p.bidirectionalPairId)) continue
    if (p.bidirectionalPairId) seen.add(p.bidirectionalPairId)
    out.push({
      id: p.id,
      name: p.name,
      signalType: p.signalType,
      connectorFormat: p.connectorFormat,
      bidirectional: !!p.bidirectional,
      direction: 'input',
    })
  }
  for (const p of device.outputs) {
    if (p.bidirectionalPairId && seen.has(p.bidirectionalPairId)) continue
    if (p.bidirectionalPairId) seen.add(p.bidirectionalPairId)
    out.push({
      id: p.id,
      name: p.name,
      signalType: p.signalType,
      connectorFormat: p.connectorFormat,
      bidirectional: !!p.bidirectional,
      direction: 'output',
    })
  }
  return out
}

function draftToDevicePorts(drafts: DraftPort[]): {
  inputs: Port[]
  outputs: Port[]
} {
  const inputs: Port[] = []
  const outputs: Port[] = []
  for (const d of drafts) {
    const base = {
      name: d.name.trim() || `${d.direction === 'input' ? 'Input' : 'Output'}`,
      signalType: d.signalType,
      connectorFormat: d.connectorFormat,
    }
    // USB is always physically bidirectional — expand even if the
    // user didn't tick the checkbox.
    const bidirectional = d.bidirectional || isBidirectionalPort(d)
    if (bidirectional) {
      const pairId = `bi-${d.id}`
      inputs.push({
        ...base,
        id: makeId('port'),
        bidirectional: true,
        bidirectionalPairId: pairId,
      })
      outputs.push({
        ...base,
        id: makeId('port'),
        bidirectional: true,
        bidirectionalPairId: pairId,
      })
    } else if (d.direction === 'input') {
      inputs.push({ ...base, id: d.id })
    } else {
      outputs.push({ ...base, id: d.id })
    }
  }
  return { inputs, outputs }
}

export const EditDeviceDialog: React.FC<EditDeviceDialogProps> = ({
  isOpen,
  onClose,
  device,
  onUpdate,
  onCreate,
  prefill,
}) => {
  const isCreating = device === null
  const [name, setName] = useState('')
  const [manufacturer, setManufacturer] = useState('')
  const [category, setCategory] = useState<DeviceCategory>('interface')
  const [drafts, setDrafts] = useState<DraftPort[]>([])

  // Re-seed the form whenever the dialog opens or switches targets.
  useEffect(() => {
    if (!isOpen) return
    if (device) {
      setName(device.name)
      setManufacturer(device.manufacturer ?? '')
      setCategory(device.category)
      setDrafts(devicePortsToDraft(device))
    } else {
      setName(prefill?.name ?? '')
      setManufacturer(prefill?.manufacturer ?? '')
      setCategory(prefill?.category ?? 'interface')
      const prefillDrafts: DraftPort[] = []
      for (const p of prefill?.inputs ?? []) {
        prefillDrafts.push({
          id: makeId('port'),
          name: p.name,
          signalType: p.signalType,
          connectorFormat: p.connectorFormat,
          bidirectional: p.bidirectional,
          direction: 'input',
        })
      }
      for (const p of prefill?.outputs ?? []) {
        prefillDrafts.push({
          id: makeId('port'),
          name: p.name,
          signalType: p.signalType,
          connectorFormat: p.connectorFormat,
          bidirectional: p.bidirectional,
          direction: 'output',
        })
      }
      setDrafts(prefillDrafts)
    }
  }, [isOpen, device, prefill])

  const canSave = name.trim().length > 0

  const handleAddPort = (direction: 'input' | 'output') => {
    setDrafts(prev => [
      ...prev,
      {
        id: makeId('port'),
        name: direction === 'input' ? 'Input' : 'Output',
        signalType: 'audio',
        direction,
      },
    ])
  }

  const handleUpdatePort = (idx: number, patch: Partial<DraftPort>) => {
    setDrafts(prev => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)))
  }

  const handleDeletePort = (idx: number) => {
    setDrafts(prev => prev.filter((_, i) => i !== idx))
  }

  const handleSave = () => {
    if (!canSave) return
    const { inputs, outputs } = draftToDevicePorts(drafts)
    if (isCreating) {
      onCreate?.({
        id: makeId('device'),
        name: name.trim(),
        manufacturer: manufacturer.trim() || undefined,
        category,
        inputs,
        outputs,
      })
    } else if (device) {
      onUpdate?.({
        ...device,
        name: name.trim(),
        manufacturer: manufacturer.trim() || undefined,
        category,
        inputs,
        outputs,
      })
    }
    onClose()
  }

  const inputDrafts = useMemo(
    () => drafts.filter(d => d.direction === 'input'),
    [drafts],
  )
  const outputDrafts = useMemo(
    () => drafts.filter(d => d.direction === 'output'),
    [drafts],
  )

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[90vh] bg-surface-raised rounded-xl shadow-high z-50 flex flex-col border border-brd-subtle">
          <div className="flex items-center justify-between px-6 py-4 border-b border-brd-subtle">
            <div>
              <Dialog.Title className="text-lg font-semibold text-content-primary">
                {isCreating ? 'Create custom device' : 'Edit device'}
              </Dialog.Title>
              <Dialog.Description className="text-xs text-content-tertiary mt-0.5">
                {isCreating
                  ? 'Build a device from scratch. You can add or remove ports as needed.'
                  : 'Rename, re-categorise, or reshape this device. Changes apply immediately.'}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="p-1.5 hover:bg-surface-sunken rounded-md transition-colors duration-150">
                <X size={20} className="text-content-tertiary" />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-content-secondary mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g., MacBook Pro 16"
                  className="w-full px-3 py-2 rounded-lg border border-brd-subtle bg-surface-sunken text-content-primary placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-brd-emphasis"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-content-secondary mb-1">
                  Manufacturer
                </label>
                <input
                  type="text"
                  value={manufacturer}
                  onChange={e => setManufacturer(e.target.value)}
                  placeholder="e.g., Apple"
                  className="w-full px-3 py-2 rounded-lg border border-brd-subtle bg-surface-sunken text-content-primary placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-brd-emphasis"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-content-secondary mb-1">
                Category
              </label>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors duration-150 flex items-center gap-1 ${
                      category === cat
                        ? 'text-white'
                        : 'bg-surface-sunken text-content-secondary hover:bg-surface-overlay border-transparent'
                    }`}
                    style={
                      category === cat
                        ? {
                            backgroundColor: CATEGORY_COLORS[cat],
                            borderColor: CATEGORY_COLORS[cat],
                          }
                        : undefined
                    }
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: CATEGORY_COLORS[cat] }}
                    />
                    {CATEGORY_LABELS[cat]}
                  </button>
                ))}
              </div>
            </div>

            <PortSection
              title="Inputs"
              drafts={inputDrafts}
              onChange={(idx, patch) =>
                handleUpdatePort(drafts.indexOf(inputDrafts[idx]), patch)
              }
              onDelete={idx =>
                handleDeletePort(drafts.indexOf(inputDrafts[idx]))
              }
              onAdd={() => handleAddPort('input')}
            />
            <PortSection
              title="Outputs"
              drafts={outputDrafts}
              onChange={(idx, patch) =>
                handleUpdatePort(drafts.indexOf(outputDrafts[idx]), patch)
              }
              onDelete={idx =>
                handleDeletePort(drafts.indexOf(outputDrafts[idx]))
              }
              onAdd={() => handleAddPort('output')}
            />
          </div>

          <div className="px-6 py-4 border-t border-brd-subtle flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary btn-md"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="btn-primary btn-md font-semibold"
            >
              {isCreating ? 'Create device' : 'Save changes'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

interface PortSectionProps {
  title: string
  drafts: DraftPort[]
  onChange: (idx: number, patch: Partial<DraftPort>) => void
  onDelete: (idx: number) => void
  onAdd: () => void
}

const PortSection: React.FC<PortSectionProps> = ({
  title,
  drafts,
  onChange,
  onDelete,
  onAdd,
}) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-content-secondary">
          {title} ({drafts.length})
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-accent-muted text-accent text-xs font-semibold hover:bg-accent/20 transition-colors duration-150"
        >
          <Plus className="w-3 h-3" />
          Add port
        </button>
      </div>
      {drafts.length === 0 ? (
        <p className="text-xs text-content-tertiary italic">
          No {title.toLowerCase()} yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {drafts.map((p, idx) => (
            <li
              key={p.id}
              className="flex items-center gap-2 bg-surface-sunken border border-brd-subtle rounded-lg px-2 py-1.5"
            >
              <input
                type="text"
                value={p.name}
                onChange={e => onChange(idx, { name: e.target.value })}
                className="flex-1 min-w-0 px-2 py-1 rounded-md bg-surface-raised border border-brd-subtle text-sm text-content-primary focus:outline-none focus:ring-1 focus:ring-accent/50"
              />
              <select
                value={p.signalType}
                onChange={e =>
                  onChange(idx, { signalType: e.target.value as SignalType })
                }
                className="px-2 py-1 rounded-md border border-brd-subtle bg-surface-raised text-xs text-content-primary"
                style={{
                  borderLeft: `4px solid ${SIGNAL_TYPE_COLORS[p.signalType]}`,
                }}
              >
                {SIGNAL_TYPES.map(st => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={p.connectorFormat ?? ''}
                onChange={e =>
                  onChange(idx, { connectorFormat: e.target.value })
                }
                placeholder="Connector"
                className="w-24 px-2 py-1 rounded-md border border-brd-subtle bg-surface-raised text-xs text-content-secondary placeholder-content-tertiary"
              />
              <label
                className="flex items-center gap-1 text-[10px] text-content-tertiary select-none cursor-pointer"
                title="Bidirectional ports (e.g. USB) appear on both input and output sides"
              >
                <input
                  type="checkbox"
                  checked={!!p.bidirectional}
                  onChange={e =>
                    onChange(idx, { bidirectional: e.target.checked })
                  }
                  className="rounded"
                />
                Bi-dir
              </label>
              <button
                type="button"
                onClick={() => onDelete(idx)}
                className="p-1.5 rounded text-content-tertiary hover:text-destructive hover:bg-destructive/5 transition-colors duration-150"
                title="Remove port"
              >
                <Trash size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
